import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, Pin } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { COLUMN_WIDTH_CLASS, drillKind, NAME_WIDTH_CLASS, type ColumnId, type ValueKey } from '../lib/columns'
import type { CellMove, CommitValue } from '../lib/editing'
import { formatMoney, isNegativeMoney, isZeroMoney } from '../lib/money'
import { editableColumns, type BudgetAffordances, type EditableColumn } from '../lib/permissions'
import type { BudgetRow, CostTree } from '../lib/tree'
import type { CostBuildingRef } from '../types'
import BillingDifferenceMark from './BillingDifferenceMark'
import CellEditor from './CellEditor'
import RowMenu, { type RowAction } from './RowMenu'

/** Name indentation by level 1–4. */
const INDENT = ['pl-1', 'pl-5', 'pl-9', 'pl-[3.25rem]']
const CLICK_DELAY_MS = 250

interface ValueCellProps {
  className: string
  editable: boolean
  onOpen?: () => void
  onEdit?: () => void
  children: React.ReactNode
}

/**
 * A money cell. A click opens the drill-down. On an editable cell a double click (or Enter / F2) edits
 * instead, so the click waits a moment to see whether a second one follows.
 */
const ValueCell: React.FC<ValueCellProps> = ({ className, editable, onOpen, onEdit, children }) => {
  const timer = useRef<number | null>(null)
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current) }, [])
  if (!onOpen && !editable) return <div role="gridcell" className={className}>{children}</div>
  const click = () => {
    if (!onOpen) return
    if (!editable) { onOpen(); return }
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => { timer.current = null; onOpen() }, CLICK_DELAY_MS)
  }
  const edit = () => {
    if (timer.current) { window.clearTimeout(timer.current); timer.current = null }
    if (editable && onEdit) onEdit()
  }
  return (
    <button
      type="button"
      role="gridcell"
      className={cn(className, 'hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring')}
      onClick={click}
      onDoubleClick={edit}
      onKeyDown={(e) => { if (editable && (e.key === 'Enter' || e.key === 'F2')) { e.preventDefault(); edit() } }}
    >
      {children}
    </button>
  )
}

interface Props {
  row: BudgetRow
  top: number
  columns: ColumnId[]
  currency: string
  locale: string
  affordances: BudgetAffordances
  visible: ReadonlySet<ColumnId>
  tree: CostTree
  buildings: CostBuildingRef[]
  /** The column being edited in this row, if any. */
  editing: EditableColumn | null
  onToggle: (id: string) => void
  onOpenCell?: (nodeId: string, key: ValueKey) => void
  onStartEdit: (nodeId: string, column: EditableColumn) => void
  onCommit: (nodeId: string, value: CommitValue, move: CellMove) => void
  onCancelEdit: () => void
  onRowAction?: (nodeId: string, action: RowAction) => void
}

const BudgetGridRow: React.FC<Props> = ({ row, top, columns, currency, locale, affordances, visible, tree, buildings, editing, onToggle, onOpenCell, onStartEdit, onCommit, onCancelEdit, onRowAction }) => {
  const { t } = useTranslation()
  const n = row.node.activity
  const levelOne = n.depth === 1
  const bg = levelOne ? 'bg-muted' : 'bg-background'
  const cell = 'flex h-full shrink-0 items-center px-2 group-hover:bg-accent'
  const editable = editableColumns(n, affordances, visible)
  const canEdit = (c: EditableColumn) => editable.includes(c)
  const keyEdit = (c: EditableColumn) => (e: React.KeyboardEvent) => {
    if (canEdit(c) && (e.key === 'Enter' || e.key === 'F2')) { e.preventDefault(); onStartEdit(n.id, c) }
  }
  const editor = (c: EditableColumn, initial: string) => (
    <CellEditor column={c} initial={initial} locale={locale} onCommit={(value, move) => onCommit(n.id, value, move)} onCancel={onCancelEdit} />
  )
  return (
    <div
      role="row"
      aria-level={n.depth}
      data-node-id={n.id}
      className={cn('group absolute left-0 flex h-8 min-w-max border-b text-sm', bg, n.kind === 'GROUP' && 'font-medium', levelOne && 'font-semibold')}
      // ui-drift: dynamic
      style={{ top }}
    >
      <div role="gridcell" className={cn('sticky left-0 z-10 gap-1 border-r pr-1', cell, bg, NAME_WIDTH_CLASS, INDENT[Math.min(Math.max(n.depth, 1), 4) - 1])}>
        {row.hasChildren ? (
          <button
            type="button"
            className="flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
            aria-label={t(row.collapsed ? 'CostControl_expand' : 'CostControl_collapse')}
            aria-expanded={!row.collapsed}
            onClick={() => onToggle(n.id)}
          >
            {row.collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        ) : (
          <span className="h-4 w-4 shrink-0" />
        )}
        {editing === 'name' ? editor('name', n.name) : (
          <span
            className="min-w-0 flex-1 truncate outline-none focus-visible:ring-2 focus-visible:ring-ring"
            title={n.name}
            tabIndex={canEdit('name') ? 0 : undefined}
            onDoubleClick={() => { if (canEdit('name')) onStartEdit(n.id, 'name') }}
            onKeyDown={keyEdit('name')}
          >
            {n.name}
          </span>
        )}
        {levelOne && n.buildingName && editing !== 'name' && (
          <span className="max-w-[8rem] shrink-0 truncate text-xs font-normal text-muted-foreground" title={n.buildingName}>{n.buildingName}</span>
        )}
        {onRowAction && editing !== 'name' && <RowMenu row={n} tree={tree} affordances={affordances} buildings={buildings} onAction={onRowAction} />}
      </div>
      {columns.map((id) => {
        if (id === 'code') {
          return (
            <div
              key={id}
              role="gridcell"
              className={cn(cell, 'text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring', COLUMN_WIDTH_CLASS.code)}
              tabIndex={canEdit('code') && editing !== 'code' ? 0 : undefined}
              onDoubleClick={() => { if (canEdit('code')) onStartEdit(n.id, 'code') }}
              onKeyDown={keyEdit('code')}
            >
              {editing === 'code' ? editor('code', n.code ?? '') : <span className="truncate">{n.kind === 'ITEM' ? n.code ?? '' : ''}</span>}
            </div>
          )
        }
        if ((id === 'originalBudget' || id === 'estimate' || id === 'projectedFinalCost') && editing === id) {
          return <div key={id} role="gridcell" className={cn(cell, COLUMN_WIDTH_CLASS[id])}>{editor(id, id === 'estimate' ? n.estimate ?? '' : n.values[id])}</div>
        }
        const negative = (id === 'variance' || id === 'billingDifference') && isNegativeMoney(n.values[id])
        const editableHere = (id === 'originalBudget' || id === 'estimate' || id === 'projectedFinalCost') && canEdit(id)
        // An item keeps its stored estimate when a contract or a manual forecast takes over; it then no longer counts.
        const isItemEstimate = id === 'estimate' && n.kind === 'ITEM'
        const notInForce = isItemEstimate && n.estimate !== null && (n.costBasedForecast || n.hasForecastOverride)
        const notInForceKey = n.costBasedForecast ? 'CostControl_estimate_not_in_force' : 'CostControl_estimate_overridden'
        return (
          <ValueCell
            key={id}
            className={cn(cell, 'justify-end gap-1 tabular-nums', COLUMN_WIDTH_CLASS[id], negative && 'text-destructive')}
            editable={editableHere}
            onOpen={onOpenCell && id !== 'estimate' && drillKind(id) !== 'none' ? () => onOpenCell(n.id, id) : undefined}
            onEdit={() => { if (id === 'originalBudget' || id === 'estimate' || id === 'projectedFinalCost') onStartEdit(n.id, id) }}
          >
            {id === 'billingDifference' && negative && <BillingDifferenceMark />}
            {id === 'projectedFinalCost' && n.hasForecastOverride && (
              <span title={t('CostControl_forecast_override')}><Pin className="h-3 w-3 text-muted-foreground" /></span>
            )}
            {notInForce ? (
              <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-muted-foreground line-through">{formatMoney(n.estimate!, { locale, currency, whole: true })}</span>
                </TooltipTrigger>
                <TooltipContent>{t(notInForceKey)}</TooltipContent>
              </Tooltip>
              </TooltipProvider>
            ) : isItemEstimate ? (
              n.estimate === null ? <span className="text-muted-foreground">—</span> : formatMoney(n.estimate, { locale, currency, whole: true })
            ) : id === 'estimate' && isZeroMoney(n.values.estimate) ? (
              <span className="text-muted-foreground">—</span>
            ) : (
              formatMoney(n.values[id], { locale, currency, whole: true })
            )}
          </ValueCell>
        )
      })}
    </div>
  )
}
export default React.memo(BudgetGridRow)
